import { useMemo, useRef, useState, useCallback, type ReactNode } from 'react'
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tab,
  TabList,
  Tabs,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableOptions, type DataTableRef } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useAccessCheck } from '@/hooks/useAccess'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { exportToCSV } from '@/lib/utils/csvUtils'
import { apiClient } from '@/lib/api/client'
import { fetchClientOptionsList } from '@/lib/services/clientContextService'
import {
  fetchBusinessUnitHierarchyIndex,
  type BusinessUnitHierarchyNode,
} from '@/lib/services/businessUnitService'
import { useClientContextStore } from '@/store/clientContext'
import { useEffect } from 'react'

function buildDeleteWarning(node: BusinessUnitHierarchyNode | undefined): string {
  if (!node?.hasChildren) return ''
  const n = node.childCount
  return ` This unit has ${n} child business unit${n === 1 ? '' : 's'}; delete or reassign ${n === 1 ? 'it' : 'them'} first or the server will reject the delete.`
}

function buildBulkDeleteWarning(
  ids: number[],
  hierarchyMap: Map<number, BusinessUnitHierarchyNode>
): string {
  const withChildren = ids.filter((id) => hierarchyMap.get(id)?.hasChildren)
  if (!withChildren.length) return ''
  return ` ${withChildren.length} selected unit(s) have child business units and may fail until children are removed or reassigned.`
}

type TabValue = 'all' | 'trash'

export default function BusinessUnitsPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const access = useAccessCheck()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const activeClientCode = useClientContextStore((s) => s.activeClientCode)
  const setActiveClient = useClientContextStore((s) => s.setActiveClient)

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [bulkAction, setBulkAction] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [clientOptions, setClientOptions] = useState<Array<{ id: number; code: string; name: string; database_name?: string }>>([])
  const [hierarchyMap, setHierarchyMap] = useState<Map<number, BusinessUnitHierarchyNode>>(new Map())

  useEffect(() => {
    fetchClientOptionsList()
      .then((clients) => {
        setClientOptions(
          clients.map((c) => ({
            id: c.id,
            code: c.code,
            name: c.name,
            database_name: c.database_name,
          }))
        )
      })
      .catch(() => setClientOptions([]))
  }, [])

  useEffect(() => {
    if (!activeClientCode) {
      setHierarchyMap(new Map())
      return
    }
    let cancelled = false
    fetchBusinessUnitHierarchyIndex(activeClientCode)
      .then((map) => {
        if (!cancelled) setHierarchyMap(map)
      })
      .catch(() => {
        if (!cancelled) setHierarchyMap(new Map())
      })
    return () => {
      cancelled = true
    }
  }, [activeClientCode])

  const customRenderers = useMemo(
    () => ({
      name: (value: unknown, row?: Record<string, unknown>): ReactNode => {
        const id = typeof row?.id === 'number' ? row.id : Number(row?.id)
        const node = Number.isFinite(id) ? hierarchyMap.get(id) : undefined
        const depth = node?.depth ?? (row?.parent_id ? 1 : 0)
        const label = value != null ? String(value) : ''
        return (
          <HStack spacing={2} align="center" flexWrap="wrap">
            <Text as="span" fontWeight={depth === 0 ? 'semibold' : 'normal'}>
              {label}
            </Text>
            {node?.hasChildren && (
              <Badge colorScheme="purple" fontSize="0.65rem" variant="subtle">
                {node.childCount} child{node.childCount === 1 ? '' : 'ren'}
              </Badge>
            )}
          </HStack>
        )
      },
    }),
    [hierarchyMap]
  )

  const getActionMessage = useCallback(
    (action: string, id: number | string): string | undefined => {
      const rowId = typeof id === 'number' ? id : Number(id)
      const node = Number.isFinite(rowId) ? hierarchyMap.get(rowId) : undefined
      const childWarn = buildDeleteWarning(node)

      if (action === 'Trash' && activeTab === 'all') {
        return `Move this business unit to trash? Linked stores remain assigned until you change them.${childWarn}`
      }
      if (action === 'Delete') {
        return `Permanently delete this business unit? This cannot be undone.${childWarn} Units with linked stores cannot be deleted until stores are reassigned.`
      }
      if (action === 'Restore') {
        return 'Restore this business unit? It will appear in the active list again.'
      }
      return undefined
    },
    [activeTab, hierarchyMap]
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        name: { name: 'Name', withSort: true, maxWidth: '220px' },
        business_code: { name: 'Business Code', withSort: true, maxWidth: '180px' },
        parent_name: { name: 'Parent', withSort: false, maxWidth: '220px' },
        active: {
          name: 'Status',
          withSort: true,
          badge: { Active: 'green', Inactive: 'yellow', true: 'green', false: 'yellow' },
          badgeLabels: { Active: 'Active', Inactive: 'Inactive', true: 'Active', false: 'Inactive' },
        },
        updated_at: { name: 'Updated At', withSort: true, maxWidth: '170px' },
      },
      dataSource: activeTab === 'trash' ? '/store-management/archived/business-unit' : '/store-management/business-unit',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: activeClientCode ? 'No business units found' : 'Select a client to load business units',
    }),
    [activeTab, activeClientCode]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: false,
    }),
    [access]
  )

  const tableParams = useMemo(() => ({ search: searchQuery, _client: activeClientCode ?? '' }), [searchQuery, activeClientCode])

  const handleSearch = () => {
    const q = searchRef.current?.value || ''
    setSearchQuery(q)
    tableRef.current?.applyFilter('search', q)
  }

  const handleBulkActionClick = () => {
    if (!activeClientCode) return showWarning('Select a client first')
    if (!bulkAction) return showWarning('Please select an action first')
    const ids = tableRef.current?.getSelectedRows() || []
    if (!ids.length) return showWarning('Please select at least one item')

    const childWarn = buildBulkDeleteWarning(ids, hierarchyMap)

    if (bulkAction === 'delete' && activeTab === 'trash') {
      setModalMessage(
        `Permanently delete ${ids.length} business unit(s)? This cannot be undone.${childWarn} Rows with linked stores will be rejected by the server.`
      )
    } else if (bulkAction === 'delete' && activeTab === 'all') {
      setModalMessage(
        `Move ${ids.length} business unit(s) to trash? Linked stores stay assigned until you reassign them.${childWarn}`
      )
    } else if (bulkAction === 'restore') {
      setModalMessage(`Restore ${ids.length} business unit(s) to the active list?`)
    } else {
      setModalMessage('Are you sure you want to apply this change?')
    }
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const ids = tableRef.current?.getSelectedRows() || []
    if (!ids.length) return
    setIsBulkProcessing(true)
    try {
      let url = ''
      if (bulkAction === 'restore') {
        url = '/store-management/business-unit/bulk/restore'
      } else if (bulkAction === 'delete') {
        url = activeTab === 'trash'
          ? '/store-management/business-unit/bulk/force-delete'
          : '/store-management/business-unit/bulk/delete'
      }
      await apiClient.post(url, { ids })
      showSuccess('Success', 'Bulk action completed')
      tableRef.current?.reload()
      if (activeClientCode) {
        fetchBusinessUnitHierarchyIndex(activeClientCode).then(setHierarchyMap).catch(() => {})
      }
      setBulkAction('')
      onClose()
    } catch (error: unknown) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const handleExportData = async () => {
    if (!activeClientCode) return showWarning('Select a client before exporting')
    setIsExporting(true)
    try {
      await exportToCSV({
        endpoint: activeTab === 'trash' ? '/store-management/archived/business-unit' : '/store-management/business-unit',
        selectedIds: [],
        activeTab,
        searchQuery: '',
        csvHeaders: ['ID', 'Name', 'Business Code', 'Description', 'Parent ID', 'Status', 'Updated At'],
        dataMapper: (row: Record<string, unknown>) => [
          row.id ?? '',
          row.name ?? '',
          row.business_code ?? '',
          row.descriptions ?? '',
          row.parent_id ?? '',
          row.active ?? '',
          row.updated_at ?? '',
        ],
        showError,
        showSuccess,
      })
    } catch (error: unknown) {
      showError(error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex={1} overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex={1} minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>Business Units</Heading>
              <Text fontSize="sm" color={colors.text} mb={2}>
                Manage parent-child business units per selected client
              </Text>
              <HStack spacing={3} align="center" flexWrap="wrap">
                <Text fontSize="sm" fontWeight="medium" color={colors.text}>Business Unit (Client)</Text>
                <Select
                  size="sm"
                  maxW="360px"
                  placeholder="Select client"
                  value={activeClientCode ?? ''}
                  onChange={(e) => {
                    const code = e.target.value
                    const selected = clientOptions.find((c) => c.code === code)
                    setActiveClient(
                      selected
                        ? { id: selected.id, code: selected.code, name: selected.name, database_name: selected.database_name }
                        : null
                    )
                  }}
                >
                  {clientOptions.map((client) => (
                    <option key={client.id} value={client.code}>
                      {client.name} ({client.code})
                    </option>
                  ))}
                </Select>
              </HStack>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={(idx) => setActiveTab(idx === 0 ? 'all' : 'trash')}>
                    <TabList borderBottom="none">
                      <Tab>All <Badge ml={2} colorScheme="blue">{totalRows}</Badge></Tab>
                      {totalTrash > 0 && <Tab>Trash <Badge ml={2} colorScheme="red">{totalTrash}</Badge></Tab>}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname="/organization/business-units"
                      onExport={handleExportData}
                      createLabel="Create Business Unit"
                      isExporting={isExporting}
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select placeholder="Bulk actions" size="sm" value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} isDisabled={!activeClientCode}>
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick} isDisabled={!activeClientCode}>Apply</Button>
                  </HStack>
                )}
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input ref={searchRef} placeholder="Search by name or code..." onKeyDown={(e) => e.key === 'Enter' && handleSearch()} pr="2.5rem" isDisabled={!activeClientCode} />
                    <InputRightElement width="2.5rem">
                      <IconButton aria-label="Search" icon={<Search size={16} />} size="sm" h="1.75rem" w="1.75rem" onClick={handleSearch} variant="ghost" isDisabled={!activeClientCode} />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody p={0} pt={1} pb={4} mx={5} flex={1} overflow="hidden" display="flex" flexDirection="column" minH={0}>
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                {activeClientCode ? (
                  <DataTable
                    key={`${activeClientCode}-${activeTab}`}
                    ref={tableRef}
                    options={tableOptions}
                    params={tableParams}
                    access={permissions}
                    customRenderers={customRenderers}
                    getActionMessage={getActionMessage}
                    setSubSub={(all, trashed) => {
                      setTotalRows(all)
                      setTotalTrash(trashed)
                    }}
                    onOpenModal={(id) => navigate(`/organization/business-units/${id}`)}
                  />
                ) : (
                  <Box py={12} textAlign="center" color={colors.text}>
                    <Text>No client selected.</Text>
                  </Box>
                )}
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody><Text>{modalMessage}</Text></ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing} loadingText="Processing...">
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}

